import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/gestion/habitaciones/")({
  component: HabitacionesPage,
});

type Habitacion = {
  id: string;
  nombre: string;
  descripcion: string;
  capacidad: number;
  precio_temporada_alta: number;
  precio_temporada_media: number;
  precio_temporada_baja: number;
  activa: boolean;
  imagen_url: string | null;
  orden: number;
};

function HabitacionesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Habitacion[]>([]);

  async function load() {
    const { data, error } = await supabase.from("habitaciones").select("*").order("orden");
    if (error) return toast.error(error.message);
    setItems((data ?? []) as Habitacion[]);
  }
  useEffect(() => { load(); }, []);

  async function remove(id: string) {
    if (!confirm("¿Eliminar esta habitación?")) return;
    const { error } = await supabase.from("habitaciones").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Eliminada");
    load();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Da de alta cada habitación o la casa completa. Pulsa una para editarla, subir fotos y ajustar precios.
        </p>
        <Button onClick={() => navigate({ to: "/gestion/habitaciones/$id", params: { id: "nueva" } })}>
          <Plus className="mr-2 h-4 w-4" />Nueva habitación
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {items.map((h) => (
          <Card key={h.id} className="transition-shadow hover:shadow-md">
            <CardHeader className="flex flex-row items-start justify-between space-y-0">
              <div>
                <CardTitle className="font-serif text-lg">
                  <Link to="/gestion/habitaciones/$id" params={{ id: h.id }} className="hover:text-primary">
                    {h.nombre}
                  </Link>
                </CardTitle>
                <p className="text-xs text-muted-foreground">Capacidad: {h.capacidad} · {h.activa ? "Activa" : "Inactiva"}</p>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" asChild>
                  <Link to="/gestion/habitaciones/$id" params={{ id: h.id }} aria-label="Editar">
                    <Pencil className="h-4 w-4" />
                  </Link>
                </Button>
                <Button size="icon" variant="ghost" onClick={() => remove(h.id)} aria-label="Eliminar">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              <p className="line-clamp-2 text-muted-foreground">{h.descripcion}</p>
              <p className="text-xs text-muted-foreground">Precios en Tarifas (según día y temporada).</p>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="text-sm text-muted-foreground">Todavía no hay habitaciones.</p>}
      </div>
    </div>
  );
}
